import { useState } from 'react';

import { useI18n } from '../../i18n/index.tsx';
import { saveNarrative } from '../../services/report.service.ts';

/**
 * The action plan, and the box a coach writes it in.
 *
 * Section 04 rendered the narrative when it existed and a placeholder when it
 * did not — and nothing in the product could ever set it, so the placeholder
 * was permanent. The reader's view is unchanged; what is new is that the
 * people who prepare these conversations can put something there.
 *
 * Only offered to a reviewer. A leader reading their own report sees the plan
 * as prose, which is what it is to them.
 */
export const ActionPlan = ({
  participantId,
  narrative,
  canEdit,
  onSaved,
}: {
  participantId: string;
  narrative: string | null;
  canEdit: boolean;
  onSaved: (narrative: string | null) => void;
}) => {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(narrative ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await saveNarrative(participantId, draft);
      onSaved(result.narrative);
      setEditing(false);
      setSavedAt(result.updatedAt);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-3 rounded-2xl border border-line bg-paper px-5 py-4 shadow-card">
        <textarea
          value={draft}
          autoFocus
          rows={10}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('report.actionPlanPlaceholder')}
          className="w-full resize-y rounded-xl border border-line bg-sand px-3.5 py-3 text-[14.5px] leading-relaxed outline-none focus:border-teal"
        />
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="cursor-pointer rounded-xl bg-teal px-3.5 py-2 text-[13px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:opacity-40"
          >
            {busy ? t('common.saving') : t('common.save')}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(narrative ?? '');
              setEditing(false);
              setError(null);
            }}
            className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
          >
            {t('common.cancel')}
          </button>
          {/* Saying it is not published is the difference between a coach
              drafting and a coach thinking the leader can already read it. */}
          <span className="ml-auto text-[12px] text-faint">{t('report.actionPlanDraftNote')}</span>
        </div>
        {error && <p className="mt-2 text-[12.5px] text-[var(--color-critical)]">{error}</p>}
      </div>
    );
  }

  return (
    <div
      className={`mt-3 rounded-2xl px-5 py-4 ${
        narrative
          ? 'border border-line bg-paper shadow-card'
          : 'border border-dashed border-line bg-paper/60'
      }`}
    >
      {narrative ? (
        <p className="text-[14.5px] leading-relaxed whitespace-pre-line">{narrative}</p>
      ) : (
        <>
          <p className="text-[14px] text-soft">{t('report.narrativePending')}</p>
          <p className="mt-1.5 text-[12.5px] text-faint">{t('report.narrativePendingHelp')}</p>
        </>
      )}

      {canEdit && (
        <div className="mt-3 flex items-center gap-3 border-t border-line-soft pt-3">
          <button
            type="button"
            onClick={() => {
              setDraft(narrative ?? '');
              setEditing(true);
            }}
            className="cursor-pointer rounded-xl border border-line bg-paper px-3.5 py-2 text-[13px] font-semibold transition hover:border-teal hover:text-teal"
          >
            {narrative ? t('report.actionPlanEdit') : t('report.actionPlanWrite')}
          </button>
          {savedAt && <span className="text-[12px] text-faint">{t('report.actionPlanSaved')}</span>}
        </div>
      )}
    </div>
  );
};
